/**
 * Who changed what, and when.
 *
 * Built around one idea: a log is only worth having if it covers the people who
 * can change the most. Administrators are in here on the same footing as
 * everybody else, and the page is readable by supervisors too - a trail only
 * its subjects can read answers "what happened" but not "who is watching the
 * watchers", and the second question is usually why the log was asked for.
 *
 * Online only, like the other ledger screens. The trail is not replicated to
 * the device: an offline copy of an audit record is a copy that can be stale
 * or absent at exactly the moment somebody is relying on it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Filter, History, Loader2, MapPin, RefreshCw, X } from 'lucide-react';
import type { ActivityRow, User } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { Card, CardBody, Code, EmptyState, Skeleton } from '../../components/ui/primitives.js';
import { Button } from '../../components/ui/button.js';
import { Input, Select } from '../../components/ui/field.js';
import { api, isNetworkError } from '../../api/client.js';
import { cn } from '../../lib/cn.js';

/** The ranges people actually ask for, rather than a date picker by default. */
const RANGES = [
  { id: 'today', label: 'Today', days: 0 },
  { id: '1d', label: 'Yesterday', days: 1 },
  { id: '3d', label: 'Last 3 days', days: 3 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: 'all', label: 'Everything', days: null },
] as const;

function startOf(days: number | null): string | null {
  if (days === null) return null;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function when(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (+d >= +today) return `Today ${time}`;
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (+d >= +yesterday) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
}

export function ActivityPage() {
  const [params, setParams] = useSearchParams();
  const range = params.get('range') ?? '7d';
  const userId = params.get('user') ?? '';
  const kind = params.get('kind') ?? '';
  const search = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<User[]>([]);

  useEffect(() => {
    void api
      .listUsers()
      .then((r) => setPeople(r.data ?? []))
      .catch(() => setPeople([]));
  }, []);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    // A custom window wins; otherwise the chosen range.
    if (from || to) {
      if (from) q.set('from', new Date(from).toISOString());
      if (to) {
        const end = new Date(to);
        end.setHours(23, 59, 59, 999);
        q.set('to', end.toISOString());
      }
    } else {
      const preset = RANGES.find((r) => r.id === range) ?? RANGES[3];
      const start = startOf(preset.days);
      if (start) q.set('from', start);
      // "Yesterday" means that day alone, not the two days since.
      if (preset.id === '1d') {
        const end = new Date();
        end.setHours(0, 0, 0, 0);
        q.set('to', end.toISOString());
      }
    }
    if (userId) q.set('userId', userId);
    if (kind) q.set('kind', kind);
    if (search.trim()) q.set('q', search.trim());
    q.set('limit', '50');
    return q;
  }, [range, userId, kind, search, from, to]);

  const load = useCallback(
    async (more = false) => {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams(query);
        if (more && cursor) q.set('cursor', cursor);
        const res = await api.listActivity(`?${q.toString()}`);
        setRows((prev) => (more && prev ? [...prev, ...res.data] : res.data));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(
          isNetworkError(e)
            ? 'Could not reach the server. The trail is kept on the server, so it needs a connection.'
            : e instanceof Error
              ? e.message
              : 'Could not load the activity log.',
        );
      } finally {
        setLoading(false);
      }
    },
    [query, cursor],
  );

  useEffect(() => {
    setCursor(null);
    void load(false);
    // Reloads whenever the filters change; `load` closes over the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const activeFilters = [userId, kind, from, to].filter(Boolean).length;

  return (
    <Page
      title="Activity"
      description="Who changed what, and when. Everyone is in here, administrators included."
      actions={
        <Button variant="secondary" onClick={() => void load(false)} disabled={loading}>
          {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Refresh
        </Button>
      }
    >
      <div className="space-y-3">
        {/* --- when --- */}
        <div className="flex flex-wrap items-center gap-1.5">
          {RANGES.map((r) => (
            <Button
              key={r.id}
              size="sm"
              variant={!from && !to && range === r.id ? 'primary' : 'secondary'}
              onClick={() => {
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('range', r.id);
                    next.delete('from');
                    next.delete('to');
                    return next;
                  },
                  { replace: true },
                );
              }}
            >
              {r.label}
            </Button>
          ))}
          <span className="mx-1 text-xs text-muted-foreground">or</span>
          <Input
            type="date"
            aria-label="From date"
            className="w-auto"
            value={from}
            onChange={(e) => setParam('from', e.target.value)}
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            aria-label="To date"
            className="w-auto"
            value={to}
            onChange={(e) => setParam('to', e.target.value)}
          />
        </div>

        {/* --- who and what --- */}
        <div className="grid gap-2 sm:grid-cols-3">
          <Select
            aria-label="Filter by person"
            value={userId}
            onChange={(e) => setParam('user', e.target.value)}
            placeholder="Everyone"
            options={people.map((p) => ({ value: p.id, label: `${p.name} (${p.email})` }))}
          />
          <Select
            aria-label="Filter by kind"
            value={kind}
            onChange={(e) => setParam('kind', e.target.value)}
            placeholder="Everything"
            options={[
              { value: 'change', label: 'Record changes' },
              { value: 'movement', label: 'Check in / check out' },
            ]}
          />
          <Input
            placeholder="Search a mould, a person, a value…"
            value={search}
            onChange={(e) => setParam('q', e.target.value)}
          />
        </div>

        {activeFilters > 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="size-3.5" />
            {activeFilters} filter{activeFilters === 1 ? '' : 's'} on
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setParams(new URLSearchParams({ range: '7d' }), { replace: true })}
            >
              <X className="size-3.5" /> Clear
            </Button>
          </div>
        ) : null}

        {error ? (
          <Card>
            <CardBody>
              <p className="text-sm text-danger">{error}</p>
            </CardBody>
          </Card>
        ) : null}

        {rows === null ? (
          <Skeleton className="h-64" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing in this window"
            description="Try a wider date range, or clear the filters."
          />
        ) : (
          <Card>
            <CardBody className="p-0">
              <ul className="divide-y divide-border">
                {rows.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-2.5 text-sm">
                    <span className="w-28 shrink-0 tabular text-xs text-muted-foreground">
                      {when(row.at)}
                    </span>
                    <span className="w-36 shrink-0 truncate font-medium" title={row.actor ?? ''}>
                      {row.actor ?? <span className="text-subtle-foreground">account removed</span>}
                    </span>
                    <span
                      className={cn(
                        'shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
                        row.kind === 'movement'
                          ? 'bg-primary-subtle text-primary'
                          : 'bg-surface-sunken text-ink',
                      )}
                    >
                      {row.action}
                    </span>
                    {row.entity === 'moulds' && row.subject ? (
                      <Link
                        to={`/moulds/${row.entityId}`}
                        className="truncate font-medium text-primary hover:underline"
                      >
                        {row.subject}
                      </Link>
                    ) : (
                      <span className="truncate">{row.subject}</span>
                    )}

                    {/* The whole point of a trail: what it was, and what it became. */}
                    {row.kind === 'change' && (row.before || row.after) ? (
                      <span className="flex items-center gap-1.5 text-xs">
                        <Code className="max-w-40 truncate">{row.before ?? 'blank'}</Code>
                        <ArrowRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                        <Code className="max-w-40 truncate">{row.after ?? 'blank'}</Code>
                      </span>
                    ) : null}

                    {row.place ? (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="size-3" aria-hidden /> {row.place}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {cursor ? (
          <div className="flex justify-center">
            <Button variant="secondary" onClick={() => void load(true)} disabled={loading}>
              {loading ? <Loader2 className="size-4 animate-spin" /> : <History className="size-4" />}
              Show older
            </Button>
          </div>
        ) : null}
      </div>
    </Page>
  );
}
